/*
  CGB – Find Your Coogee quiz page steps (sections/cgb-quiz-page.liquid).
  Start → questions → email → loading → results, all on one URL. Results are shown by <cgb-quiz-results> on the same page.
  The root gets data-cgb-first="true" on the first screen (start, or question 1 when there is no start screen),
  so the CSS can show the mobile image banner there only.
  Needs assets/cgb-quiz.js (window.CgbQuiz).
*/
if (!customElements.get('cgb-quiz-page')) {
  class CgbQuizPage extends HTMLElement {
    connectedCallback() {
      if (!window.CgbQuiz) return;
      this.Quiz = window.CgbQuiz;
      this.steps = Array.from(this.querySelectorAll('[data-cgb-step="question"]'));
      this.startStep = this.querySelector('[data-cgb-step="start"]');
      this.emailStep = this.querySelector('[data-cgb-step="email"]');
      this.loadingStep = this.querySelector('[data-cgb-step="loading"]');
      this.form = this.querySelector('[data-cgb-email-form]');
      this.input = this.form && this.form.querySelector('[name="contact[email]"]');
      this.submitBtn = this.form && this.form.querySelector('[data-cgb-submit]');
      this.errorEl = this.querySelector('[data-cgb-email-error]');
      this.loadingMs = parseInt(this.dataset.loadingMs, 10) || 1800;
      this.emailEnabled = this.dataset.emailEnabled === 'true' && !!this.form;
      this.designMode = this.dataset.designMode === 'true';
      this.answers = {};
      this.index = 0;

      this.addEventListener('click', this.onClick.bind(this));
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', () => this.updateSubmitState());
      }

      this.onRestart = () => this.restart();
      this.onShowResults = () => { if (this.Quiz.getSaved()) this.showResults(); else this.restart(); };
      document.addEventListener('cgb-quiz:restart', this.onRestart);
      document.addEventListener('cgb-quiz:show-results', this.onShowResults);

      this.onBlockSelect = (e) => {
        if (e.detail.sectionId !== this.dataset.sectionId) return;
        var idx = this.steps.findIndex((s) => s.dataset.blockId === e.detail.blockId);
        if (idx > -1) this.showQuestion(idx, false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.start();
    }

    disconnectedCallback() {
      document.removeEventListener('cgb-quiz:restart', this.onRestart);
      document.removeEventListener('cgb-quiz:show-results', this.onShowResults);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* ----- entry point ----- */
    start() {
      if (this.designMode) {
        // Theme Editor: show the step chosen in "Preview in editor"; the results section shows a sample below.
        var preview = this.dataset.editorPreview;
        if (preview === 'email' && this.emailStep) return this.showEmail(false);
        if (preview === 'loading') return this.setScreen(this.loadingStep, false);
        if (preview === 'start' && this.startStep) return this.showStart(false);
        return this.showQuestion(0, false);
      }
      var retake = window.location.hash === '#retake';
      if (retake) history.replaceState(null, '', window.location.pathname + window.location.search);
      var params = new URLSearchParams(window.location.search);
      if (!retake && (this.Quiz.getSaved() || params.get('bike'))) return this.showResults(false);
      // A retake skips the start screen (they've seen it already).
      if (this.startStep && !retake) return this.showStart(false);
      this.showQuestion(0, false);
    }

    restart() {
      this.answers = {};
      this.match = null;
      this.busy = false;
      this.querySelectorAll('[aria-pressed="true"]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      this.withResults((r) => r.hide());
      this.hidden = false;
      this.showQuestion(0, true);
    }

    /* ----- screens ----- */
    setScreen(screen, scroll) {
      this.hidden = false;
      [this.startStep].concat(this.steps, [this.emailStep, this.loadingStep]).forEach((el) => {
        if (el) el.hidden = el !== screen;
      });
      var first = screen === this.startStep || (!this.startStep && screen === this.steps[0]);
      this.setAttribute('data-cgb-first', first ? 'true' : 'false');
      this.setAttribute('data-cgb-current', screen ? screen.dataset.cgbStep : '');
      if (scroll) this.scrollToTop();
      var heading = screen && screen.querySelector('[data-cgb-step-heading]');
      if (heading && scroll) heading.focus({ preventScroll: true });
    }

    showStart(scroll = true) {
      this.index = -1;
      this.setScreen(this.startStep, scroll);
    }

    showQuestion(idx, scroll = true) {
      this.index = idx;
      var step = this.steps[idx];
      if (!step) return;
      this.setScreen(step, scroll);
      var back = step.querySelector('[data-cgb-back]');
      // On question 1, Back returns to the start screen (if there is one).
      if (back) back.hidden = idx === 0 && !this.startStep;
    }

    showEmail(scroll = true) {
      this.index = this.steps.length;
      this.setScreen(this.emailStep, scroll);
      this.showError('');
      this.updateSubmitState();
    }

    showResults(scroll = true) {
      this.withResults((r) => {
        this.hidden = true;
        r.show();
        if (scroll) r.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    withResults(fn) {
      var el = document.querySelector('cgb-quiz-results');
      if (!el) return;
      customElements.whenDefined('cgb-quiz-results').then(() => fn(el));
    }

    scrollToTop() {
      var rect = this.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight * 0.4) return;
      var headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--m-header-height'), 10) || 0;
      window.scrollTo({ top: Math.max(0, rect.top + window.scrollY - headerOffset - 10), behavior: 'smooth' });
    }

    /* ----- interaction ----- */
    onClick(e) {
      if (e.target.closest('[data-cgb-start]')) { e.preventDefault(); return this.showQuestion(0, true); }
      if (e.target.closest('[data-cgb-back]')) { e.preventDefault(); return this.goBack(); }
      if (e.target.closest('[data-cgb-skip]')) { e.preventDefault(); return this.finish('skipped'); }
      var answer = e.target.closest('[data-cgb-answer]');
      if (answer) this.choose(answer);
    }

    goBack() {
      if (this.busy) return;
      if (this.index >= this.steps.length) this.showQuestion(this.steps.length - 1);
      else if (this.index === 0 && this.startStep) this.showStart(true);
      else if (this.index > 0) this.showQuestion(this.index - 1);
    }

    choose(button) {
      if (this.busy || this.designMode) {
        if (this.designMode) button.setAttribute('aria-pressed', 'true');
        return;
      }
      var step = button.closest('[data-cgb-step="question"]');
      step.querySelectorAll('[data-cgb-answer]').forEach((b) => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
      this.answers[step.dataset.key] = button.dataset.cgbAnswer;
      this.busy = true;
      setTimeout(() => {
        this.busy = false;
        if (this.index < this.steps.length - 1) this.showQuestion(this.index + 1);
        else this.complete();
      }, 250);
    }

    /* Bikes the results section could render (their product exists in this market) */
    availableBikes(inStockOnly) {
      var sel = 'cgb-quiz-results [data-cgb-bike]' + (inStockOnly ? '[data-in-stock="true"]' : '');
      var keys = Array.from(document.querySelectorAll(sel)).map((el) => el.dataset.cgbBike);
      return keys.length || inStockOnly ? keys : null;
    }

    complete() {
      var match = this.Quiz.computeMatch(this.answers);
      // Skip bikes that aren't available here, so tags, Omnisend and the results all use the bike that's shown.
      // Prefer bikes that exist in this market AND are in stock (sold out only if nothing else is).
      var picked = this.Quiz.pickAvailable(match.ranking, this.availableBikes(false), this.availableBikes(true)) || this.Quiz.pickAvailable(match.ranking, null);
      this.match = { bike: picked.bike, alt: picked.alt, note: match.note, ranking: picked.ranking };
      var saved = this.Quiz.getSaved();
      var alreadySubmitted = !!saved && saved.email === 'submitted';
      // Someone who already gave their email (e.g. on a retake) isn't asked again.
      if (this.emailEnabled && !alreadySubmitted) this.showEmail();
      else this.finish(alreadySubmitted ? 'submitted' : 'skipped');
    }

    updateSubmitState() {
      if (!this.submitBtn) return;
      var valid = this.Quiz.isValidEmail(this.input.value);
      this.submitBtn.setAttribute('aria-disabled', valid ? 'false' : 'true');
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    /*
      This is the page's only Shopify signup form (also used by the results "Unlock" box, source 'results-unlock').
      A valid email is NOT prevented: the browser submits the form, Shopify's hCaptcha runs, and the page reloads.
      cgb-quiz.js (finishSignup) then checks Shopify's answer and sends Omnisend only on success.
    */
    onSubmit(e) {
      // Shopify's captcha may submit the form again after its check: let that one through untouched.
      if (this.form.dataset.cgbSubmitting === 'true') return;
      var source = this.form.dataset.cgbSource || 'quiz';
      var bike = source === 'quiz' ? (this.match && this.match.bike) : this.form.dataset.cgbBike;
      var email = this.input.value;
      if (this.designMode || (source === 'quiz' && (this.busy || !this.match))) {
        e.preventDefault();
        return;
      }
      if (!this.Quiz.isValidEmail(email)) {
        e.preventDefault();
        this.showError(this.dataset.errorInvalid);
        this.input.focus();
        return;
      }
      this.showError('');
      // Save the result first: after the reload the visitor lands straight on their results.
      if (source === 'quiz') this.saveResult('pending');
      this.Quiz.startSignup(this.form, email, bike, source);
      this.form.dataset.cgbSubmitting = 'true';
      this.submitBtn.classList.add('m-spinner-loading');
      // Normally the page has reloaded long before this. If not (e.g. the captcha was closed), allow a new try.
      setTimeout(() => {
        delete this.form.dataset.cgbSubmitting;
        this.submitBtn.classList.remove('m-spinner-loading');
      }, 15000);
    }

    saveResult(emailState) {
      return this.Quiz.save({
        bike: this.match.bike,
        alt: this.match.alt,
        note: this.match.note,
        ranking: this.match.ranking,
        answers: this.answers,
        email: emailState,
        resultsUrl: window.location.pathname
      });
    }

    finish(emailState) {
      if (!this.match) return;
      this.saveResult(emailState);
      this.setScreen(this.loadingStep, true);
      this.busy = true;
      setTimeout(() => {
        this.busy = false;
        this.showResults(true);
      }, this.loadingMs);
    }
  }
  customElements.define('cgb-quiz-page', CgbQuizPage);
}
